import { axiosInstance } from '../../api/api';

/**
 * Fetch quality defect details for a given VIN
 * GET /quality/{vin}
 *
 * Response shape:
 * {
 *   vin, asn,
 *   open:   { partDefects: [], machineDefects: [], manualDefects: [] },
 *   closed: { partDefects: [], machineDefects: [], manualDefects: [] }
 * }
 *
 * @param {string} vin
 * @param {function} successCallback
 * @param {function} errorCallback
 */
export const fetchQualityDefectDetails = (
  vin,
  successCallback,
  errorCallback,
) => {
  return () => {
    axiosInstance
      .get(`/quality/${vin}`)
      .then(({ data }) => {
        successCallback(data);
      })
      .catch((err) => {
        errorCallback(err);
      });
  };
};

/**
 * Create a new defect (manual)
 * POST /quality
 * Payload: { serialNumber, sequenceNumber, defectType, defectTs, reason, remark, defectStatus, raisedBy }
 *
 * @param {Object} payload - { serialNumber, sequenceNumber, reason, remark, raisedBy }
 * @param {function} successCallback
 * @param {function} errorCallback
 */
export const createDefect = (
  payload,
  successCallback,
  errorCallback,
) => {
  return () => {
    const body = {
      serialNumber: payload.serialNumber,
      sequenceNumber: payload.sequenceNumber,
      defectType: 'MANUAL',
      defectTs: new Date().toISOString(),
      defectReason: payload.reason || '',
      defectRemark: payload.remark || '',
      defectStatus: 'OPEN',
      defectSkippedBy: payload.raisedBy || '',
      defectCheckedBy: payload.raisedBy || '',
    };
    axiosInstance
      .post('/quality', body)
      .then(({ data }) => {
        successCallback(data);
      })
      .catch((err) => {
        errorCallback(err);
      });
  };
};

/**
 * Close defects for quality operation (supports multiple defect IDs)
 * POST /quality/closeDefects
 * Payload: [{ mesDefectId, closeRemark, closeReason, closedBy }, ...]
 *
 * @param {Array<number|{defectId?:number, mesDefectId?:number, closeReason?:string, closeRemark?:string, closedBy?:string}>} defects
 * @param {function} successCallback
 * @param {function} errorCallback
 */
export const closeDefect = (
  defects,
  successCallback,
  errorCallback,
) => {
  return () => {
    const normalizedDefects = (Array.isArray(defects) ? defects : [])
      .filter((d) => d !== null && d !== undefined)
      .map((d) => {
        if (typeof d === 'number')
          return {
            mesDefectId: d,
            closeReason: '',
            closeRemark: '',
            closedBy: '',
          };
        return {
          mesDefectId: d?.mesDefectId ?? d?.defectId,
          closeReason: d?.closeReason ?? '',
          closeRemark: d?.closeRemark ?? '',
          closedBy: d?.closedBy ?? '',
        };
      })
      .filter((d) => typeof d.mesDefectId === 'number');

    axiosInstance
      .post(`/quality/closeDefects`, normalizedDefects)
      .then(({ data }) => {
        successCallback(data);
      })
      .catch((err) => {
        errorCallback(err);
      });
  };
};

/**
 * Fetch WTCL VIN/KSPEC attributes
 * GET /station/wtcl/vin-kspec-attributes/{serialNumber}
 *
 * @param {string} serialNumber
 * @param {function} successCallback
 * @param {function} errorCallback
 */
export const fetchEngineSerialNumber = (
  serialNumber,
  successCallback,
  errorCallback,
) => {
  return () => {
    axiosInstance
      .get(`/station/wtcl/vin-kspec-attributes?serialNumber=${serialNumber}`)
      .then(({ data }) => {
        // Normalize API response to the shape WTCL already expects.
        // Expected response:
        // {
        //   serialNumber, kspec, colour, description, engineSerialNumber
        // }
        successCallback({
          serialNumber: data?.serialNumber ?? serialNumber,
          kspec: data?.kspec ?? '',
          colour: data?.colour ?? data?.color ?? '',
          description: data?.description ?? '',
          engineSerialNumber: data?.engineSerialNumber ?? '',
          // Back-compat fields (WTCL mapping uses these with fallbacks).
          model: data?.description ?? '',
          partNumber: data?.kspec ?? '',
          ipmsDate: data?.ipmsDate ?? '',
        });
      })
      .catch((err) => {
        errorCallback(err);
      });
  };
};
